import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { ModalBody, ModalFooter, ModalHeader, injectModalData, injectModalRef } from '../../modal';
import { MilaMultiSelectFetcher, MilaMultiSelectOption } from '../mila-multi-select-option.model';

export interface MilaMultiSelectSheetData {
  title: string;
  placeholder: string;
  fetchPage: MilaMultiSelectFetcher;
  selected: MilaMultiSelectOption[];
  /** Same meaning as the MilaMultiSelect inputs. */
  multiple?: boolean;
  maxSelected?: number | null;
}

/**
 * Phone version of mila-multi-select: the dropdown would hang off the field
 * and run under the playground's sticky run bar, so phones pick in a bottom
 * sheet instead (search, picked chips, checkbox list with infinite scroll).
 * Works on a copy of the selection: Done closes with it, Clear closes with
 * [], and ✕/backdrop close with nothing (caller keeps what it had).
 */
@Component({
  selector: 'mila-multi-select-sheet',
  standalone: true,
  imports: [ModalHeader, ModalBody, ModalFooter],
  templateUrl: './mila-multi-select-sheet.html',
  styleUrl: './mila-multi-select-sheet.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MilaMultiSelectSheet implements OnInit {
  readonly data = injectModalData<MilaMultiSelectSheetData>();
  readonly modalRef = injectModalRef<MilaMultiSelectOption[]>();

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly query$ = new Subject<string>();

  selected: MilaMultiSelectOption[] = [...this.data.selected];
  options: MilaMultiSelectOption[] = [];
  queryText = '';
  isLoading = false;
  hasMore = false;
  limitMessage = '';
  private page = 0;

  ngOnInit(): void {
    this.query$
      .pipe(debounceTime(250), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(query => this.loadPage(query, 0, true));
    this.loadPage('', 0, true);
  }

  onInput(value: string): void {
    this.queryText = value;
    this.query$.next(value);
  }

  onListScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40 && this.hasMore && !this.isLoading) {
      this.loadPage(this.queryText, this.page + 1, false);
    }
  }

  isSelected(option: MilaMultiSelectOption): boolean {
    return this.selected.some(o => o.id === option.id);
  }

  toggle(option: MilaMultiSelectOption): void {
    this.limitMessage = '';
    if (this.isSelected(option)) {
      this.remove(option);
    } else if (this.data.multiple === false) {
      this.selected = [option];
    } else if (this.data.maxSelected != null && this.selected.length >= this.data.maxSelected) {
      this.limitMessage = `You can add up to ${this.data.maxSelected} items.`;
    } else {
      this.selected = [...this.selected, option];
    }
  }

  remove(option: MilaMultiSelectOption): void {
    this.selected = this.selected.filter(o => o.id !== option.id);
  }

  private loadPage(query: string, page: number, reset: boolean): void {
    if (this.isLoading && !reset) return;
    this.isLoading = true;
    this.data.fetchPage(query, page)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          // A newer search may have started while this page was in flight.
          if (query !== this.queryText) return;
          this.options = reset ? res.items : [...this.options, ...res.items];
          this.hasMore = res.hasMore;
          this.page = page;
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading = false;
          this.hasMore = false;
          this.cdr.markForCheck();
        },
      });
  }
}
